import { AlertWarningIcon, Card, css, Inline, Pill, styled, Tooltip } from 'canoe';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { DocumentPreview } from '@candu-ai/document/preview';
import { CONTENT_PUBLISHED_STATES } from 'commons/models/notebook';
import TimeAgo from 'dashboard/components/TimeAgo';
import SegmentName from 'dashboard/containers/SegmentName';
import ContentPublishedState from 'dashboard/pages/Content/components/ContentPublishedState';
import { DEFAULT_SEGMENT } from 'dashboard/pages/Content/constants';
import { Drag } from 'styleguide/icons';

interface CardProps {
  placeholderPosition: 'right' | 'left' | true | false;
  isHoverable: boolean;
}

const StyledCard = styled(Card)<CardProps>`
  ${({ theme, placeholderPosition }) => css`
    display: flex;
    flex-direction: column;
    height: 330px;
    padding: 0;
    position: relative;
    width: 308px;
    cursor: pointer;

    ${placeholderPosition &&
    `
      &::after {
        content: '';
        height: 100%;
        border: 1px dashed ${theme.colors.OnyxGray};
        position: absolute;
        ${placeholderPosition === 'left' ? 'left: -9px;' : 'right: -9px;'}
      }
    `}
  `}
`;

interface DraggableContainerProps {
  isDraggable: boolean;
}

const DraggableContainer = styled.div<DraggableContainerProps>`
  ${({ theme, isDraggable }) => css`
    align-items: center;
    background-color: ${isDraggable ? theme.colors.GlitterGrayL10 : 'transparent'};
    border-radius: 4px 4px 0 0;
    cursor: ${isDraggable ? 'grab' : 'pointer'};
    display: flex;
    justify-content: center;
    height: 21px;
    flex-shrink: 0;

    span {
      margin-top: -4px;
    }
  `}
`;

const Header = styled.div`
  ${({ theme }) => css`
    align-items: center;
    border-bottom: 1px solid ${theme.colors.GlitterGrayD10};
    display: flex;
    padding: 1rem;
  `}
`;

const IndexIndicator = styled.p`
  ${({ theme }) => css`
    align-items: center;
    background-color: ${theme.colors.GlitterGray};
    border-radius: 50%;
    color: ${theme.colors.OnyxGrayL20};
    display: flex;
    font-weight: 600;
    height: 28px;
    justify-content: center;
    margin-bottom: 0;
    margin-right: 8px;
    width: 28px;
    flex-shrink: 0;
  `}
`;

const SegmentDescription = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;
  flex-shrink: 1;
  overflow: hidden;
`;

const Flex = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: ${theme.spaces.xxs};
  `}
`;

const SegmentInfo = styled.p`
  margin-bottom: 0;
  flex-shrink: 0;
`;

const SegmentLink = styled.div`
  ${({ theme }) => css`
    min-width: 0;
    flex-grow: 0;
    flex-shrink: 1;
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.BrandPurple};

    button {
      font-size: ${theme.fonts.body.fontSize};
      overflow: hidden;
      width: 100%;
    }
  `}
`;

const Transform = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  padding: 6px;
`;

const DocumentContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const Footer = styled.div`
  ${({ theme }) => css`
    align-items: center;
    border-top: 1px solid ${theme.colors.GlitterGrayD10};
    display: flex;
    height: 44px;
    justify-content: center;
  `}
`;

interface StateProps {
  semaphoreOnly?: boolean;
  tutorial?: any;
}

const State = styled(ContentPublishedState)<StateProps>`
  height: 6px;
  margin-right: 6px;
  margin-top: 6px;
  width: 6px;
`;

const getDragDirection = (dragIndex: number, hoverIndex: number): 'right' | 'left' | boolean => {
  if (dragIndex < hoverIndex) {
    return 'right';
  }
  if (dragIndex > hoverIndex) {
    return 'left';
  }

  return false;
};

interface Props {
  index: number;
  segment: any;
  documentId: number;
  applicationId: string;
  segmentExists: boolean;
  disabled?: boolean;
  onSelectSegment: (segmentId: string) => void;
  onUpdate?: ({
    currentIndex,
    destinationIndex,
  }: {
    currentIndex: number;
    destinationIndex: number;
  }) => void;
}

export const SegmentCard = ({
  segment,
  documentId,
  applicationId,
  index,
  segmentExists,
  disabled,
  onUpdate,
  onSelectSegment,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ dragCard }, dragRef] = useDrag({
    type: 'segments-card-identifier',
    item: {
      index,
      segmentId: segment.segmentId,
    },
    collect: (monitor) => ({
      dragCard: monitor.getItem(),
    }),
    canDrag: !disabled && !!segment.segmentId,
  });

  const [{ isOver }, dropRef] = useDrop({
    accept: 'segments-card-identifier',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
    drop(item: { index: number; segmentId: string }) {
      const destinationIndex = index;
      if (item.index === destinationIndex) {
        return;
      }

      if (onUpdate) {
        onUpdate({ currentIndex: item.index, destinationIndex });
      }
    },
    canDrop: () => !!segment.segmentId,
  });

  const handleSelectSegment = () => {
    onSelectSegment(segment.segmentId || DEFAULT_SEGMENT.id);
  };

  return (
    <div>
      <StyledCard
        onClick={handleSelectSegment}
        isHoverable
        placeholderPosition={isOver && segment.segmentId && getDragDirection(dragCard.index, index)}
        ref={dragRef(dropRef(ref))}
      >
        <DraggableContainer isDraggable={!disabled && !!segment.segmentId}>
          {!!segment.segmentId && !disabled && <Drag width="12px" color="neutral" shade={2} />}
        </DraggableContainer>
        <Header>
          <IndexIndicator>{index + 1}</IndexIndicator>
          <SegmentDescription>
            <Flex>
              <SegmentInfo>The Segment </SegmentInfo>

              {!segmentExists ? (
                <Tooltip
                  content={
                    <>
                      This Segment is no longer available.
                      <br />
                      Please, click to delete or replace it.
                    </>
                  }
                >
                  <Pill variant="warning">
                    <Inline space="xxs" align="center">
                      <AlertWarningIcon color="MangoOrangeD10" size="s" />
                      was deleted.
                    </Inline>
                  </Pill>
                </Tooltip>
              ) : (
                <SegmentLink>
                  <SegmentName linkToSegment={!!segment.segmentId} segmentId={segment.segmentId} />
                </SegmentLink>
              )}
            </Flex>

            <SegmentInfo>will see this content:</SegmentInfo>
          </SegmentDescription>
        </Header>

        <DocumentContainer>
          <Transform>
            {/* TODO: look into getting a version id for the document here */}
            <DocumentPreview applicationId={applicationId} documentId={documentId} />
          </Transform>
        </DocumentContainer>
        <Footer>
          <State
            semaphoreOnly
            content={{
              lastModifiedAt: segment.lastModifiedAt,
              lastPublishedAt: segment.lastPublishedAt,
              publishedState:
                segment.lastPublishedAt === segment.lastModifiedAt
                  ? CONTENT_PUBLISHED_STATES.PUBLISHED
                  : CONTENT_PUBLISHED_STATES.DRAFT,
            }}
          />
          {segment.lastModifiedAt ? <TimeAgo date={segment.lastModifiedAt} /> : 'never'}
        </Footer>
      </StyledCard>
    </div>
  );
};
